import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep, merge } from 'lodash';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import { muiGlobalTheme } from '@themes';
import { FormControl, FormHelperText, FormControlLabel, Checkbox as MuiCheckbox } from '@mui/material';
import { colors } from '@custom-components';
import { Label } from '../Label';

const customizeMuiGlobalTheme = (styledProps) => {
  const {
    labelPointerEvents = 'auto',
    checkboxPointerEvents = 'auto',
    paddingRight = '12px',
    labelMarginTop = 'initial',
    width = 'auto',
    minWidth = 'auto'
  } = styledProps;

  const newCheckBoxTheme = cloneDeep(muiGlobalTheme);
  newCheckBoxTheme.components = merge(newCheckBoxTheme.components, {
    MuiCheckbox: {
      styleOverrides: {
        root: {
          height: 44,
          marginBottom: 0,
          pointerEvents: checkboxPointerEvents,
          paddingRight,
          color: colors.colorGrey03
        },
        colorPrimary: {
          '&$checked': {
            color: colors.colorBlue
          },
          '&$disabled': {
            color: colors.colorGrey04
          }
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          alignItems: 'flex-start',
          pointerEvents: labelPointerEvents
        },
        label: {
          marginTop: labelMarginTop
        }
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width,
          minWidth
        }
      }
    }
  });
  return createTheme(newCheckBoxTheme);
};

function Checkbox(props) {
  const {
    label,
    customLabel,
    description,
    toolTip,
    className,
    disabled,
    disableRipple,
    name,
    checked,
    onChange,
    indeterminate,
    variant,
    helperText,
    onFocus,
    InputProps,
    icon,
    checkedIcon,
    styledProps,
    errorText
  } = props;

  const { labelColor, labelOpacity } = styledProps;

  const checkBoxTheme = useRef(customizeMuiGlobalTheme(styledProps));

  const handleOnChange = (_, newChecked) => {
    onChange({ target: { name, value: newChecked, checked: newChecked } });
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={checkBoxTheme.current}>
        <FormControl>
          <FormControlLabel
            className={className}
            style={{ opacity: labelOpacity }}
            control={
              <MuiCheckbox
                icon={icon}
                checkedIcon={checkedIcon}
                color="primary"
                disabled={disabled}
                name={name}
                checked={checked}
                onChange={handleOnChange}
                indeterminate={indeterminate}
                variant={variant}
                helperText={helperText}
                onFocus={onFocus}
                InputProps={InputProps}
                disableRipple={disableRipple}
                data-testid={`${name}-checkbox`}
              />
            }
            label={
              customLabel || (
                <Label
                  labelColor={labelColor}
                  disabled={disabled}
                  label={label}
                  description={description}
                  toolTip={toolTip}
                />
              )
            }
          />
          {errorText && <FormHelperText error>{errorText}</FormHelperText>}
        </FormControl>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

Checkbox.propTypes = {
  /** Optional title that appears above label  */
  description: PropTypes.string,
  /** Optional prop for passing tooltip component  */
  toolTip: PropTypes.node,
  /** Optional prop for passing custom label fragment / component  */
  customLabel: PropTypes.node,

  label: PropTypes.string,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  indeterminate: PropTypes.bool,
  disableRipple: PropTypes.bool,
  variant: PropTypes.string,
  helperText: PropTypes.string,
  className: PropTypes.string,
  onFocus: PropTypes.func,
  InputProps: PropTypes.shape({}),
  icon: PropTypes.element,
  checkedIcon: PropTypes.element,
  styledProps: PropTypes.shape({
    pointerEvents: PropTypes.string,
    labelPointerEvents: PropTypes.string,
    checkboxPointerEvents: PropTypes.string,
    labelColor: PropTypes.string,
    labelOpacity: PropTypes.number,
    labelMarginTop: PropTypes.string,
    width: PropTypes.string,
    minWidth: PropTypes.string
  }),
  errorText: PropTypes.string
};

Checkbox.defaultProps = {
  description: undefined,
  toolTip: null,
  customLabel: undefined,
  label: '',
  disabled: false,
  name: '',
  checked: false,
  onChange: () => {},
  disableRipple: false,
  indeterminate: false,
  variant: '',
  helperText: '',
  className: '',
  onFocus: () => {},
  InputProps: {},
  icon: undefined,
  checkedIcon: undefined,
  styledProps: {
    pointerEvents: 'auto',
    labelPointerEvents: 'auto',
    checkboxPointerEvents: 'auto',
    labelColor: '',
    labelOpacity: 1,
    labelMarginTop: 'initial',
    width: 'auto',
    minWidth: 'auto'
  },
  errorText: ''
};

export default Checkbox;
